<template>
  <div v-if="isVisible" class="overlay">
    <div class="overlay-backdrop" @click="close"></div>
    <div class="overlay-content">
      <div class="overlay-header">
        <h2>Add Reading Device</h2>
        <button class="close-button" @click="close" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Prevent and stop native submit bubbling -->
      <form @submit.prevent.stop="submitDevice" class="device-form">
        <div class="form-group">
          <div class="label-container">
            <label class="form-label">Identification Number</label>
            <div class="hint-icon" title="Unique identifier for the reading device">?</div>
          </div>
          <div class="input-container">
            <input
                type="text"
                v-model="deviceData.identification_number"
                class="form-input"
                placeholder="Enter device ID"
                required
            />
          </div>
        </div>

        <div class="form-group">
          <div class="label-container">
            <label class="form-label">Device Type</label>
            <div class="hint-icon" title="Type of reading device (e.g., Gateway, Mobile, etc.)">?</div>
          </div>

          <div class="device-type-list-container">
            <div class="device-type-list">
              <div
                  v-for="type in deviceTypes"
                  :key="type.value"
                  :class="['device-type-item', { selected: deviceData.type === type.value }]"
                  @click.prevent="selectDeviceType(type)"
              >
                <div class="device-type-radio">
                  <input
                      type="radio"
                      :id="`type-${type.value}`"
                      :value="type.value"
                      v-model="deviceData.type"
                      name="deviceType"
                  />
                  <label :for="`type-${type.value}`"></label>
                </div>
                <div class="device-type-info">
                  <div class="device-type-name">{{ type.label }}</div>
                  <div class="device-type-description">{{ type.description }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isWatertechUser" class="form-group">
          <div class="label-container">
            <label class="form-label">Tenant ID (Optional)</label>
            <div class="hint-icon" title="Assign device to a specific tenant">?</div>
          </div>
          <div class="input-container">
            <input
                type="text"
                v-model="deviceData.tenant_id"
                class="form-input"
                placeholder="Leave empty for current tenant"
            />
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="action-button cancel-button" @click="close">
            Cancel
          </button>
          <button type="submit" class="action-button submit-button" :disabled="isSubmitting || !deviceData.type">
            {{ isSubmitting ? 'Creating...' : 'Create Device' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddReadingDeviceOverlay',

  // ✅ Declare component events so Vue treats @submit/@close as component events,
  // not native DOM listeners on the root element.
  emits: ['submit', 'close'],

  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      deviceData: {
        identification_number: '',
        type: '',
        tenant_id: ''
      },
      isSubmitting: false,
      deviceTypes: [
        { value: 'gatewaylora', label: 'Gateway Lora', description: 'LoRa wireless communication gateway' },
        { value: 'webdyneasy', label: 'WebDynEasy', description: 'WebDyn Easy reading device' },
        { value: 'wtsontex', label: 'WT sontex', description: 'Sontex WaterTech device' }
      ]
    };
  },
  computed: {
    isWatertechUser() {
      const tenantId = localStorage.getItem('tenant_id');
      return tenantId === 'watertech';
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetForm();
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  methods: {
    resetForm() {
      this.deviceData = { identification_number: '', type: '', tenant_id: '' };
      this.isSubmitting = false;
    },
    selectDeviceType(type) {
      this.deviceData.type = type.value;
    },
    close() {
      this.$emit('close');
    },
    async submitDevice() {
      if (this.isSubmitting) {
        console.log('Already submitting, ignoring duplicate submission');
        return;
      }

      if (!this.deviceData.identification_number || !this.deviceData.identification_number.trim()) {
        alert('Identification number is required');
        return;
      }
      if (!this.deviceData.type) {
        alert('Device type is required');
        return;
      }

      this.isSubmitting = true;
      console.log('Submitting device:', this.deviceData);

      try {
        const submitData = {
          identification_number: this.deviceData.identification_number.trim(),
          type: this.deviceData.type
        };
        if (this.isWatertechUser && this.deviceData.tenant_id && this.deviceData.tenant_id.trim()) {
          submitData.tenant_id = this.deviceData.tenant_id.trim();
        }

        console.log('Final submit data:', submitData);
        this.$emit('submit', submitData); // component event (not DOM)

      } catch (error) {
        console.error('Error preparing device data:', error);
        alert('Failed to prepare device data. Please try again.');
        this.isSubmitting = false;
      }
    },
    onSubmitSuccess() {
      this.resetForm();
      this.close();
    },
    onSubmitError(errorMessage) {
      this.isSubmitting = false;
      alert(errorMessage || 'Failed to create device. Please try again.');
    }
  }
};
</script>

<style scoped>
/* Overlay */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.overlay-content {
  position: relative;
  width: 500px;
  max-width: 90%;
  max-height: 85vh;
  border-radius: 8px;
  padding: 32px;
  color: white;
  background-color: rgba(50, 50, 50, 0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
  overflow-y: auto;
}

/* Header */
.overlay-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.overlay-header h2 {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255, 255, 255, 0.9);
}

/* Form */
.device-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.hint-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 10px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s, color 0.2s;
}

.hint-icon:hover {
  background-color: rgba(79, 195, 247, 0.6);
  color: white;
}

.input-container {
  padding-left: 25px;
}

.form-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* Device Type List */
.device-type-list-container {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px;
  margin-left: 25px;
}

.device-type-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.device-type-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.device-type-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.device-type-item.selected {
  background: rgba(79, 195, 247, 0.1);
  border: 1px solid rgba(79, 195, 247, 0.3);
}

.device-type-radio input[type="radio"] {
  display: none;
}

.device-type-radio label {
  display: block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
}

.device-type-item.selected .device-type-radio label {
  border-color: #4fc3f7;
}

.device-type-item.selected .device-type-radio label::after {
  content: '';
  width: 8px;
  height: 8px;
  background: #4fc3f7;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.device-type-info {
  flex: 1;
}

.device-type-name {
  font-size: 14px;
  font-weight: 600;
  color: #4fc3f7;
}

.device-type-description {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 2px;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 20px;
  border-radius: 4px;
  transition: all 0.2s;
}

.cancel-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
  border: 1px solid rgba(79, 195, 247, 0.5);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79, 195, 247, 0.3);
  border-color: rgba(79, 195, 247, 0.7);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 500px) {
  .overlay-content {
    padding: 24px;
  }

  .input-container,
  .device-type-list-container {
    padding-left: 0;
    margin-left: 0;
  }
}
</style>